import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Autocomplete,
  Slide,
  Typography,
  InputAdornment,
  Tooltip
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Componente de transición
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GuardarFlujoDialog = ({
  open,
  onClose,
  onSave,
  flowName,
  setFlowName,
  flowDescription,
  setFlowDescription,
  flowContextId,
  setFlowContextId,
  contextOptions = [],
  // Nuevos props para número de WhatsApp/Twilio
  flowWhatsappNumber = "",
  setFlowWhatsappNumber = () => {},
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm" // Más compacto
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px #6366f122",
          border: "1.5px solid #e0e7ef",
          p: 0,
          minWidth: 380,
          maxWidth: 440, // Compacto
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "rgba(23, 66, 136, 0.98)",
          fontWeight: 800,
          fontSize: 17,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          pb: 0.5,
          pt: 1.5,
          px: 2,
          letterSpacing: 1,
          background: "#fff",
          borderBottom: "1px solid #e0e7ef"
        }}
      >
        <EditNoteIcon sx={{ fontSize: 20, color: "rgba(23, 66, 136, 0.98)" }} />
        Guardar flujo
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, px: 2, pb: 0 }}>
        <Stack spacing={1.2} mt={2}>
          <Stack direction="row" spacing={1}>
            <TextField
              label="Nombre"
              value={flowName}
              onChange={e => setFlowName(e.target.value)}
              required
              variant="outlined"
              size="small"
              sx={{
                fontSize: 13,
                minWidth: 180,
                maxWidth: 220,
                "& .MuiInputBase-input": { fontSize: 13, py: 0.7 },
              }}
              InputProps={{
                sx: {
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#334155",
                  background: "#fff",
                  borderRadius: 2,
                  px: 1,
                  py: 0.5,
                }
              }}
              InputLabelProps={{
                sx: { color: "rgba(23, 66, 136, 0.98)", fontWeight: 600, fontSize: 13 }
              }}
            />
            
            <Autocomplete
              options={[...contextOptions].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }))}
              getOptionLabel={opt => opt.nombre}
              value={contextOptions.find(opt => opt.id === flowContextId) || null}
              onChange={(e, newValue) => setFlowContextId(newValue ? newValue.id : "")}
              filterOptions={(options, { inputValue }) =>
                options.filter(opt =>
                  opt.nombre.toLowerCase().startsWith(inputValue.trim().toLowerCase())
                )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Contexto"
                  required
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: 13,
                    minWidth: 100,
                    maxWidth: 140,
                    "& .MuiInputBase-input": { fontSize: 13, py: 0.7 },
                  }}
                  InputLabelProps={{ sx: { color: "rgba(23, 66, 136, 0.98)", fontWeight: 600, fontSize: 13 } }}
                />
              )}
              sx={{ minWidth: 100, maxWidth: 140 }}
            />
          </Stack>
          
          <TextField
            label="Descripción (opcional)"
            value={flowDescription}
            onChange={e => setFlowDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={3}
            variant="outlined"
            size="small"
            sx={{
              fontSize: 13,
              maxWidth: 400, // Limita el ancho del campo descripción
              textAlign: "left", // <-- Fuerza alineación en el TextField
              "& .MuiInputBase-input": { fontSize: 13, py: 0.7, textAlign: "left" },
              "& textarea": { textAlign: "left !important" }, // <-- Fuerza alineación en el textarea
            }}
            InputProps={{
              sx: {
                fontSize: 13,
                color: "#334155",
                background: "#f1f5f9",
                borderRadius: 2,
                px: 1,
                py: 0.5,
                textAlign: "left", // <-- Cambia aquí
              }
            }}
            inputProps={{
               style: { textAlign: "left" } // <-- Fuerza alineación en el textarea
            }}
            InputLabelProps={{
              sx: { color: "rgba(23, 66, 136, 0.98)", fontWeight: 600, fontSize: 13 }
            }}
          />

          {/* Campo de número WhatsApp/Twilio */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              label="Número WhatsApp (Twilio)"
              value={flowWhatsappNumber}
              onChange={e => setFlowWhatsappNumber(e.target.value)}
              placeholder="whatsapp:+573001234567"
              variant="outlined"
              size="small"
              sx={{
                fontSize: 13,
                flex: 1,
                "& .MuiInputBase-input": { fontSize: 13, py: 0.7 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsAppIcon sx={{ color: "#25D366", fontSize: 18 }} />
                  </InputAdornment>
                ),
                sx: {
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#334155",
                  background: "#fff",
                  borderRadius: 2,
                  px: 1,
                  py: 0.5,
                }
              }}
              InputLabelProps={{
                sx: { color: "rgba(23, 66, 136, 0.98)", fontWeight: 600, fontSize: 13 }
              }}
            />
            <Tooltip
              title="Ingresa el número de WhatsApp de Twilio que recibirá los mensajes para este chatbot. Formato: whatsapp:+[código país][número]"
              arrow
              placement="top"
            >
              <InfoOutlinedIcon sx={{ color: "#64748b", fontSize: 18, cursor: "help" }} />
            </Tooltip>
          </Stack>

          {flowWhatsappNumber && (
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: 11, pl: 1 }}>
              Los mensajes enviados a este número activarán este flujo conversacional.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 2, pb: 1, pt: 1 }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            borderColor: "#e0e7ef",
            color: "#334155",
            background: "#fff",
            fontSize: 13,
            "&:hover": { background: "#f1f5f9", borderColor: "rgba(23, 66, 136, 0.98)", color: "rgba(23, 66, 136, 0.98)" }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!flowName.trim()}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            background: "rgba(23, 66, 136, 0.98)",
            color: "#fff",
            boxShadow: "none",
            fontSize: 13,
            "&:hover": { background: "#22c55e", color: "#fff" }
          }}
          startIcon={<CheckCircleIcon />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuardarFlujoDialog;